import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Syringe, Pill, FlaskConical, Droplets, Wind, ChevronRight, Shield, Award, Globe, Sparkles } from "lucide-react";
import ScrollReveal from "@/components/ScrollReveal";
import ProductCard from "@/components/ProductCard";
import products, { categories } from "@/data/products";

const fadeUp = { initial: { opacity: 0, y: 30 }, animate: { opacity: 1, y: 0 } };

const categoryIcons = {
  "Injectables": Syringe,
  "Tablets": Pill,
  "Capsules": Shield,
  "Infusions": Droplets,
  "Oral Solutions": FlaskConical,
  "Respules": Wind,
};

const categoryColors = {
  "Injectables": "#2DD4BF",
  "Tablets": "#3B82F6",
  "Capsules": "#A78BFA",
  "Infusions": "#F59E0B",
  "Oral Solutions": "#10B981",
  "Respules": "#06B6D4",
};

const stats = [
  { value: "500+", label: "Products", icon: Shield },
  { value: "6", label: "Categories", icon: Award },
  { value: "Pan India", label: "Distribution", icon: Globe },
  { value: "AI-Powered", label: "Drug Knowledge", icon: Sparkles },
];

export default function Home() {
  const featuredProducts = products.filter(p => ["drunem", "capsodru-70", "lindru-5", "anidru", "voridru", "micadru-100"].includes(p.id));

  return (
    <div data-testid="home-page">
      {/* ===== HERO ===== */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[#020617]" />
          <div className="hero-gradient absolute inset-0" />
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#2DD4BF]/5 rounded-full blur-[120px]" />
          <div className="absolute bottom-1/3 right-1/4 w-72 h-72 bg-[#3B82F6]/5 rounded-full blur-[100px]" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center pt-24 pb-32">
          <motion.div {...fadeUp} transition={{ duration: 0.7, delay: 0.2 }}>
            <div className="inline-flex items-center gap-2.5 px-5 py-2.5 rounded-full border border-white/10 bg-white/5 mb-10">
              <img src="/logo.jpg" alt="" className="w-5 h-5 rounded-full" />
              <span className="text-xs font-semibold text-[#94A3B8] tracking-wide">DRUKST BIOTECH PVT. LTD.</span>
            </div>
          </motion.div>

          <motion.h1
            {...fadeUp}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-extrabold leading-[0.95] tracking-tight mb-8"
          >
            <span className="text-white">Your Health is</span>
            <br />
            <span className="gradient-text">Our Concern</span>
          </motion.h1>

          <motion.p
            {...fadeUp}
            transition={{ duration: 0.7, delay: 0.45 }}
            className="text-base md:text-lg text-[#94A3B8] max-w-2xl mx-auto mb-10 leading-relaxed"
          >
            Delivering premium pharmaceutical solutions backed by innovation, quality, and commitment.
            Trusted by healthcare professionals across India with 500+ products.
          </motion.p>

          <motion.div
            {...fadeUp}
            transition={{ duration: 0.7, delay: 0.6 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <Link
              to="/products"
              data-testid="hero-explore-btn"
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-[#2DD4BF] text-[#022C22] font-bold text-sm tracking-wide hover:scale-105 transition-transform duration-300 shadow-lg shadow-[#2DD4BF]/20"
            >
              Explore Products
              <ArrowRight size={16} />
            </Link>
            <Link
              to="/about"
              data-testid="hero-about-btn"
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full border border-white/15 text-white/80 font-medium text-sm hover:border-[#2DD4BF]/50 hover:text-[#2DD4BF] transition-all duration-300"
            >
              About Us
            </Link>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <div className="w-5 h-8 rounded-full border border-white/20 flex items-start justify-center p-1">
            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ repeat: Infinity, duration: 1.5 }}
              className="w-1 h-2 rounded-full bg-[#2DD4BF]"
            />
          </div>
        </motion.div>
      </section>

      {/* ===== STATS BAR ===== */}
      <section className="border-y border-white/5 bg-white/[0.01]">
        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, i) => (
              <ScrollReveal key={stat.label} delay={i * 80}>
                <div className="text-center" data-testid={`stat-${stat.label.toLowerCase().replace(/\s/g,'-')}`}>
                  <stat.icon size={20} className="mx-auto mb-3 text-[#2DD4BF]/60" />
                  <p className="text-2xl md:text-3xl font-extrabold text-white">{stat.value}</p>
                  <p className="text-xs text-[#94A3B8] mt-1 tracking-wider uppercase">{stat.label}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===== PRODUCT CATEGORIES ===== */}
      <section className="py-24 px-6" data-testid="categories-section">
        <div className="max-w-7xl mx-auto">
          <ScrollReveal>
            <div className="flex items-end justify-between mb-16">
              <div>
                <p className="text-xs font-bold tracking-[0.2em] text-[#2DD4BF] uppercase mb-3">Browse by Category</p>
                <h2 className="text-3xl md:text-5xl font-extrabold text-white">
                  Product Range
                </h2>
              </div>
              <Link
                to="/products"
                data-testid="categories-view-all"
                className="hidden md:flex items-center gap-2 text-sm text-[#94A3B8] hover:text-[#2DD4BF] transition-colors"
              >
                View All Products <ArrowRight size={14} />
              </Link>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {categories.map((cat, i) => {
              const Icon = categoryIcons[cat.name] || Package;
              const color = categoryColors[cat.name] || "#2DD4BF";
              return (
                <ScrollReveal key={cat.id} delay={i * 60}>
                  <Link
                    to={`/products?category=${encodeURIComponent(cat.name)}`}
                    data-testid={`category-${cat.id}`}
                    className="group relative flex flex-col justify-between p-7 md:p-8 rounded-xl border border-white/5 bg-white/[0.02] hover:border-white/15 transition-all duration-500 min-h-[160px]"
                  >
                    <div className="flex items-start justify-between">
                      <div className="p-3 rounded-lg bg-white/5 group-hover:bg-white/10 transition-colors">
                        <Icon size={22} style={{ color }} />
                      </div>
                      <ChevronRight size={18} className="text-white/20 group-hover:text-white/60 group-hover:translate-x-1 transition-all" />
                    </div>
                    <div className="mt-auto">
                      <h3 className="text-lg font-bold text-white mb-1">{cat.name}</h3>
                    </div>
                    <div className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                      style={{ background: `radial-gradient(circle at 30% 70%, ${color}08 0%, transparent 70%)` }}
                    />
                  </Link>
                </ScrollReveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== DIVIDER ===== */}
      <div className="section-line max-w-7xl mx-auto" />

      {/* ===== AI ASSISTANT BANNER ===== */}
      <section className="py-20 px-6" data-testid="ai-banner">
        <div className="max-w-7xl mx-auto">
          <ScrollReveal>
            <div className="relative p-10 md:p-14 rounded-2xl border border-[#2DD4BF]/15 bg-gradient-to-br from-[#2DD4BF]/[0.03] to-transparent overflow-hidden">
              <div className="absolute top-0 right-0 w-72 h-72 bg-[#2DD4BF]/5 rounded-full blur-[100px]" />
              <div className="relative z-10 grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                <div>
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#2DD4BF]/10 border border-[#2DD4BF]/20 mb-5">
                    <Sparkles size={14} className="text-[#2DD4BF]" />
                    <span className="text-[11px] font-bold text-[#2DD4BF] tracking-wider uppercase">AI-Powered</span>
                  </div>
                  <h3 className="text-2xl md:text-3xl font-extrabold text-white mb-4">
                    Ask DrukstAI About Any Salt
                  </h3>
                  <p className="text-[#94A3B8] leading-relaxed mb-6">
                    Search by generic salt name like <strong className="text-white">Meropenem</strong>, <strong className="text-white">Caspofungin</strong>, or <strong className="text-white">Pantoprazole</strong> and
                    instantly get detailed pharmaceutical knowledge, research, and the matching Drukst Biotech product.
                  </p>
                  <p className="text-sm text-[#94A3B8]">
                    Click the <span className="text-[#2DD4BF] font-semibold">sparkle button</span> in the bottom right to start.
                  </p>
                </div>
                <div className="hidden md:flex justify-center">
                  <div className="w-64 p-5 rounded-xl bg-[#0F172A] border border-white/5">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-6 h-6 rounded-full bg-[#2DD4BF]/15 flex items-center justify-center">
                        <Sparkles size={12} className="text-[#2DD4BF]" />
                      </div>
                      <span className="text-xs font-bold text-white">DrukstAI</span>
                    </div>
                    <p className="text-[11px] text-[#94A3B8] leading-relaxed">
                      "Meropenem is available as <span className="text-[#2DD4BF] font-semibold">DRUNEM</span> by Drukst Biotech. It's a broad-spectrum carbapenem antibiotic..."
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* ===== DIVIDER ===== */}
      <div className="section-line max-w-7xl mx-auto" />

      {/* ===== ABOUT PREVIEW ===== */}
      <section className="py-24 px-6" data-testid="about-preview">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <ScrollReveal>
            <div>
              <p className="text-xs font-bold tracking-[0.2em] text-[#2DD4BF] uppercase mb-3">About Drukst Biotech</p>
              <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-6 leading-tight">
                Where Innovation<br />Meets Care
              </h2>
              <p className="text-[#94A3B8] leading-relaxed mb-4">
                Drukst Biotech Pvt. Ltd. is a trusted name in pharmaceuticals, delivering high-quality, affordable healthcare solutions
                across Injectables, Tablets, Capsules, Infusions, and more.
              </p>
              <p className="text-[#94A3B8] leading-relaxed mb-8">
                With 500+ products across 6 categories and pan-India distribution, we ensure timely support for healthcare providers across diverse communities.
              </p>
              <Link
                to="/about"
                data-testid="about-learn-more"
                className="inline-flex items-center gap-2 text-sm text-[#2DD4BF] font-semibold hover:gap-3 transition-all"
              >
                Learn More About Us <ArrowRight size={14} />
              </Link>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={200}>
            <div className="relative">
              <div className="aspect-[4/3] rounded-xl overflow-hidden border border-white/5">
                <img
                  src="https://images.unsplash.com/photo-1563213126-a4273aed2016?auto=format&fit=crop&w=800&q=80"
                  alt="Pharmaceutical laboratory"
                  className="w-full h-full object-cover opacity-70"
                  loading="lazy"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 p-6 rounded-xl glass-strong max-w-[250px]">
                <p className="text-2xl font-extrabold text-[#2DD4BF]">500+</p>
                <p className="text-xs text-[#94A3B8] mt-1">Premium pharmaceutical products across 6 categories</p>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* ===== DIVIDER ===== */}
      <div className="section-line max-w-7xl mx-auto" />

      {/* ===== FEATURED PRODUCTS ===== */}
      <section className="py-24 px-6" data-testid="featured-products">
        <div className="max-w-7xl mx-auto">
          <ScrollReveal>
            <div className="flex items-end justify-between mb-14">
              <div>
                <p className="text-xs font-bold tracking-[0.2em] text-[#2DD4BF] uppercase mb-3">Featured</p>
                <h2 className="text-3xl md:text-5xl font-extrabold text-white">
                  Premium Health Solutions
                </h2>
              </div>
              <Link
                to="/products"
                data-testid="featured-view-all"
                className="hidden md:flex items-center gap-2 text-sm text-[#94A3B8] hover:text-[#2DD4BF] transition-colors"
              >
                View All <ArrowRight size={14} />
              </Link>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {featuredProducts.map((product, i) => (
              <ScrollReveal key={product.id} delay={i * 80}>
                <ProductCard product={product} index={i} />
              </ScrollReveal>
            ))}
          </div>

          <div className="mt-10 text-center md:hidden">
            <Link
              to="/products"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/10 text-sm text-[#94A3B8] hover:text-white hover:border-white/30 transition-all"
            >
              View All Products <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="py-24 px-6" data-testid="cta-section">
        <div className="max-w-4xl mx-auto text-center">
          <ScrollReveal>
            <div className="relative p-12 md:p-16 rounded-2xl border border-white/5 bg-gradient-to-br from-white/[0.03] to-transparent overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-[#2DD4BF]/5 rounded-full blur-[80px]" />
              <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-4 relative z-10">
                Your Health is Our Concern
              </h2>
              <p className="text-[#94A3B8] max-w-lg mx-auto mb-8 relative z-10">
                Partner with Drukst Biotech for reliable pharmaceutical solutions. Let's make healthcare better together.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 relative z-10">
                <Link
                  to="/contact"
                  data-testid="cta-contact-btn"
                  className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-[#2DD4BF] text-[#022C22] font-bold text-sm hover:scale-105 transition-transform"
                >
                  Get in Touch <ArrowRight size={14} />
                </Link>
                <Link
                  to="/blog"
                  className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full border border-white/15 text-white/80 text-sm hover:border-[#2DD4BF]/40 transition-colors"
                >
                  Read Our Blog
                </Link>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
}
